import React, { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import API_BASE_URL from "../config";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Container,

  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Divider,
  AppBar,
  Toolbar,
  FormControl,
  Select,
  MenuItem,
  Slider,
  TextField,
  CircularProgress,
  Breadcrumbs,
  FormGroup,
  FormControlLabel,
  Checkbox,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Home as HomeIcon,
  BarChart as BarChartIcon,
  RecordVoiceOver as RecordVoiceOverIcon,
  Build as BuildIcon,
  CalendarToday as CalendarTodayIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  InfoOutlined as InfoOutlinedIcon,
  FileDownload as FileDownloadIcon,
  Edit as EditIcon,
  Favorite as FavoriteIcon,
  Language as LanguageIcon,
  Logout as LogoutIcon,
} from "@mui/icons-material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Bar,
  ComposedChart,
} from "recharts";
import { useTranslation } from "react-i18next";
import BackButton from "./BackButton";

// --- Reusable Sub-components ---
const StatCard = ({ title, value, icon, color }) => (
  <Paper
    elevation={3}
    sx={{ p: 2, display: "flex", alignItems: "center", borderRadius: "12px" }}
  >
    <Box
      sx={{
        bgcolor: color,
        borderRadius: "50%",
        p: 1.5,
        mr: 2,
        display: "flex",
        color: "white",
      }}
    >
      {icon}
    </Box>
    <Box>
      <Typography color="text.secondary">{title}</Typography>
      <Typography variant="h5" sx={{ fontWeight: "bold" }}>
        {value}
      </Typography>
    </Box>
  </Paper>
);
const ProgressRing = ({ value, max }) => {
  const percentage = max > 0 ? (value / max) * 100 : 0;
  return (
    <Box sx={{ position: "relative", display: "inline-flex" }}>
      <CircularProgress
        variant="determinate"
        value={100}
        sx={{ color: "#e0e0e0" }}
        size={60}
        thickness={4}
      />
      <CircularProgress
        variant="determinate"
        value={percentage}
        sx={{ position: "absolute", left: 0 }}
        size={60}
        thickness={4}
      />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: "absolute",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography
          variant="caption"
          component="div"
          color="text.secondary"
        >{`${Math.round(percentage)}%`}</Typography>
      </Box>
    </Box>
  );
};
const ActivityCard = ({ activity, onAddScore, onViewDetails, hasScoredToday, todayScore }) => (
  <Paper
    elevation={3}
    sx={{ p: 2, borderRadius: "12px", textAlign: "center", height: "100%" }}
  >
    <Typography variant="h6" sx={{ fontWeight: "bold", minHeight: "64px" }}>
      {activity.name}
    </Typography>
    <ProgressRing value={activity.currentScore} max={activity.maxScore} />
    <Typography sx={{ my: 1, fontWeight: "bold", fontSize: "1.2rem" }}>
      {activity.currentScore}/{activity.maxScore}
    </Typography>
    <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mt: 2 }}>
      {hasScoredToday ? (
          <Button
            variant="contained"
            color="warning"
            size="small"
            onClick={() => onAddScore(activity, todayScore)}
          >
            Edit Score: {todayScore}
          </Button>
      ) : (
          <Button
            variant="contained"
            size="small"
            onClick={() => onAddScore(activity, null)}
          >
            Add Score
          </Button>
      )}
      <Button
        variant="outlined"
        size="small"
        onClick={() => onViewDetails(activity)}
      >
        View Details
      </Button>
    </Box>
  </Paper>
);

// --- Main Therapy Component ---
const Therapy = () => {
  const { t, i18n } = useTranslation();
  const { childId, therapyTypeId } = useParams();
  const navigate = useNavigate();
  const reportRef = useRef();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const [childDetails, setChildDetails] = useState(null);
  const [therapyTypes, setTherapyTypes] = useState([]);
  const [therapyDetails, setTherapyDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    overallProgress: "0%",
    totalSessions: 0,
    lastAssessment: "N/A",
  });
  const [activeActivities, setActiveActivities] = useState([]);
  const [allAvailableActivities, setAllAvailableActivities] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [comparisonChartData, setComparisonChartData] = useState([]);
  const [scoreModalOpen, setScoreModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [changeModalOpen, setChangeModalOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [newScore, setNewScore] = useState("");
  const [isEditingScore, setIsEditingScore] = useState(false);
  const [activitiesToChange, setActivitiesToChange] = useState(new Set());
  const [sessionDate, setSessionDate] = useState(new Date().toISOString().slice(0, 10));

  const fetchData = async () => {
    // This is the main change: The faulty guard clause is removed.
    setLoading(true);
    try {
      const childRes = await axios.get(
        `${API_BASE_URL}/get_children.php?child_id=${childId}`
      );
      setChildDetails(childRes.data[0]);

      if (!therapyTypeId) {
        const therapyTypesRes = await axios.get(
          `${API_BASE_URL}/get_therapy_types.php`
        );
        const role = localStorage.getItem('userRole');
        if (role && role !== 'admin') {
          const filtered = therapyTypesRes.data.filter(t => t.name.toLowerCase().includes(role.toLowerCase()));
          setTherapyTypes(filtered);
        } else {
          setTherapyTypes(therapyTypesRes.data);
        }
      } else {
        const typesRes = await axios.get(
          `${API_BASE_URL}/get_therapy_types.php`
        );
        const currentTherapy = typesRes.data.find(
          (t) => t.id === Number(therapyTypeId)
        );
        setTherapyDetails(currentTherapy);

        const activitiesRes = await axios.get(
          `${API_BASE_URL}/get_activities.php?therapy_type_id=${therapyTypeId}`
        );
        const allActivities = activitiesRes.data.map((act) => ({
          ...act,
          currentScore: 0,
          maxScore: 10,
          history: [],
        }));
        setAllAvailableActivities(allActivities);

        const progressRes = await axios.get(
          `${API_BASE_URL}/get_therapy_progress.php?child_id=${childId}&therapy_type_id=${therapyTypeId}`
        );
        const progressData = progressRes.data;

        const dedupedProgressMap = {};
        progressData.forEach((item) => {
            const key = `${item.session_date}_${item.activity_id}_${item.scorer_type}`;
            dedupedProgressMap[key] = item;
        });
        const cleanProgressData = Object.values(dedupedProgressMap).sort((a,b) => new Date(a.session_date) - new Date(b.session_date));

        const doctorScoredActivityIds = new Set();
        cleanProgressData.forEach((item) => {
          if (item.scorer_type === "doctor") {
            doctorScoredActivityIds.add(Number(item.activity_id));
          }
        });

        const activityMap = {};
        allActivities.forEach((act) => (activityMap[act.id] = { ...act }));
        cleanProgressData.forEach((item) => {
          if (activityMap[item.activity_id]) {
            activityMap[item.activity_id].history.push(item);
            activityMap[item.activity_id].currentScore = item.score;
          }
        });

        const currentlyActive = allActivities.filter((act) =>
          doctorScoredActivityIds.has(act.id)
        );
        setActiveActivities(currentlyActive.map((act) => activityMap[act.id]));
        setActivitiesToChange(new Set(currentlyActive.map((a) => a.id)));

        const doctorDates = {};
        const comparisonDates = {};
        cleanProgressData.forEach((item) => {
          const activityName = activityMap[item.activity_id]?.name;
          if (!activityName) return;

          // Build comparison chart data
          if (!comparisonDates[item.session_date]) {
            comparisonDates[item.session_date] = { date: item.session_date };
          }
          const isDoctor = item.scorer_type === "doctor";
          const keyName = `${activityName} (${isDoctor ? "Doc" : "Parent"})`;
          comparisonDates[item.session_date][keyName] = item.score;

          // Build doctor strictly chart data
          if (isDoctor) {
            if (!doctorDates[item.session_date]) {
              doctorDates[item.session_date] = { date: item.session_date };
            }
            doctorDates[item.session_date][activityName] = item.score;
          }
        });

        setChartData(Object.values(doctorDates).sort((a,b) => new Date(a.date) - new Date(b.date)));
        setComparisonChartData(Object.values(comparisonDates).sort((a,b) => new Date(a.date) - new Date(b.date)));

        const uniqueSessions = new Set(cleanProgressData.map((p) => p.session_date));
        const totalScores = currentlyActive.reduce(
          (sum, act) => sum + activityMap[act.id].currentScore,
          0
        );
        const totalMax = currentlyActive.reduce(
          (sum, act) => sum + act.maxScore,
          0
        );
        const overallProgress =
          totalMax > 0 ? Math.round((totalScores / totalMax) * 100) : 0;
          
        const doctorScores = cleanProgressData
          .filter((p) => p.scorer_type === "doctor")
          .sort((a, b) => new Date(b.session_date) - new Date(a.session_date));
        const lastScoredDate = doctorScores.length > 0 ? new Date(doctorScores[0].session_date).toLocaleDateString() : "N/A";

        setStats({
          overallProgress: `${overallProgress}%`,
          totalSessions: uniqueSessions.size,
          lastAssessment: lastScoredDate,
        });
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childId, therapyTypeId]);

  const handleAddScoreClick = (activity, existingScore) => {
    setSelectedActivity(activity);
    if (existingScore !== null && existingScore !== undefined) {
      setNewScore(existingScore);
      setIsEditingScore(true);
    } else {
      setNewScore("");
      setIsEditingScore(false);
    }
    setScoreModalOpen(true);
  };
  const handleViewDetailsClick = (activity) => {
    setSelectedActivity(activity);
    setDetailsModalOpen(true);
  };
  const handleModalClose = () => {
    setScoreModalOpen(false);
    setDetailsModalOpen(false);
    setChangeModalOpen(false);
    setSelectedActivity(null);
    setNewScore("");
  };
  const handleScoreSubmit = async () => {
    if (!selectedActivity || newScore === "") return;
    try {
      await axios.post(`${API_BASE_URL}/manage_therapy_session.php`, {
        child_id: childId,
        therapy_type_id: therapyTypeId,
        session_date: sessionDate,
        scorer_type: "doctor",
        activities: activeActivities.map((act) => ({
          activity_id: act.id,
          score: act.id === selectedActivity.id ? newScore : act.currentScore,
          notes: act.id === selectedActivity.id ? "Scored by doctor" : "Carried over from previous session",
        })),
      });
      fetchData();
    } catch (error) {
      console.error("Failed to submit score:", error);
    }
    handleModalClose();
  };
  const handleLogout = () => {
    localStorage.removeItem("userRole");
    navigate("/login");
  };
  const changeLanguage = (event) => {
    i18n.changeLanguage(event.target.value);
  };
  const getTherapyIcon = (typeName) => {
    switch (typeName.toLowerCase()) {
      case "behavioral":
        return <BarChartIcon sx={{ fontSize: 40, color: "#2196f3" }} />;
      case "speech":
        return <RecordVoiceOverIcon sx={{ fontSize: 40, color: "#9c27b0" }} />;
      case "occupational":
        return <BuildIcon sx={{ fontSize: 40, color: "#4caf50" }} />;
      default:
        return <BarChartIcon sx={{ fontSize: 40, color: "#2196f3" }} />;
    }
  };
  const getTherapyDescription = (typeName) => {
    switch (typeName.toLowerCase()) {
      case "behavioral":
        return "Focus on modifying behaviors through positive reinforcement.";
      case "speech":
        return "Improve communication skills and language development.";
      case "occupational":
        return "Enhance daily living skills and motor development.";
      default:
        return "Comprehensive therapy for child development.";
    }
  };
  const handleActivitySelectionChange = (event) => {
    const { name, checked } = event.target;
    const activityId = Number(name);
    setActivitiesToChange((prev) => {
      const newSet = new Set(prev);
      if (checked) newSet.add(activityId);
      else newSet.delete(activityId);
      return newSet;
    });
  };
  const handleSaveChanges = () => {
    const updatedActiveActivities = allAvailableActivities.filter((act) =>
      activitiesToChange.has(act.id)
    );
    setActiveActivities(updatedActiveActivities);
    handleModalClose();
  };
  const handleDownloadPdf = () => {
    window.print();
  };

  if (loading) {
    return (
      <Container sx={{ textAlign: "center", mt: 5 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!therapyTypeId) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
          bgcolor: "#f5f5f5",
        }}
      >
        <AppBar
          position="static"
          color="default"
          elevation={1}
          sx={{ bgcolor: "white" }}
        >
          <Toolbar sx={{ justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  bgcolor: "#00bcd4",
                  borderRadius: "8px",
                  p: 0.5,
                  display: "flex",
                }}
              >
                <FavoriteIcon sx={{ color: "white", fontSize: "20px" }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                {t("akidscare_brand")}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <LanguageIcon sx={{ color: "text.secondary" }} />
                <FormControl variant="standard" sx={{ minWidth: 60 }}>
                  <Select
                    value={i18n.language}
                    onChange={changeLanguage}
                    disableUnderline
                  >
                    <MenuItem value="en">EN</MenuItem>
                    <MenuItem value="te">TE</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Button
                color="inherit"
                onClick={handleLogout}
                startIcon={<LogoutIcon />}
              >
                {t("logout_button")}
              </Button>
            </Box>
          </Toolbar>
        </AppBar>
        <Container
          maxWidth="md"
          sx={{
            py: 6,
            flexGrow: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
        <Box sx={{ width: "100%", maxWidth: 800 }}>
          <BackButton />
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Paper
              elevation={3}
              sx={{ bgcolor: "white", p: 4, borderRadius: "16px", width: "100%", maxWidth: 600 }}
            >
              <Typography
                variant="h4"
                component="h1"
                sx={{ fontWeight: "bold", textAlign: "center", mb: 1 }}
              >
                Select Analysis Type
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{ textAlign: "center", mb: 4, color: "text.secondary" }}
              >
                Choose a therapy to analyze for {childDetails?.full_name}.
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {therapyTypes.map((type) => (
                  <Paper
                    component={Link}
                    to={`/therapy/${childId}/${type.id}`}
                    key={type.id}
                    elevation={1}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      p: 3,
                      borderRadius: "12px",
                      cursor: "pointer",
                      textDecoration: "none",
                      color: "inherit",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        bgcolor: "#f5f5f5",
                        borderColor: "#00bcd4",
                        boxShadow: 3,
                      },
                    }}
                  >
                    <Box sx={{ mr: 3 }}>{getTherapyIcon(type.name)}</Box>
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        {type.name} Analysis
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {getTherapyDescription(type.name)}
                      </Typography>
                    </Box>
                  </Paper>
                ))}
              </Box>
            </Paper>
          </Box>
        </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{
      bgcolor: "#f4f6f8",
      minHeight: "100vh",
      py: 4,
    }}>
      <style>{`@media print { body * { visibility: hidden; } #report-content, #report-content * { visibility: visible; } #report-content { position: absolute; left: 0; top: 0; width: 100%; } .no-print { display: none; } .MuiPaper-root { box-shadow: none !important; border: 1px solid #ddd; } }`}</style>
      <Container maxWidth="xl">
        <BackButton className="no-print" sx={{ display: { xs: 'flex' } }} />
        <Box id="report-content" ref={reportRef}>
          <Box className="no-print" sx={{ mb: 3 }}>
            <Breadcrumbs aria-label="breadcrumb">
              <Link
                component={RouterLink}
                to={`/therapy/${childId}`}
                style={{ textDecoration: "none", color: "inherit" }}
              >
                <HomeIcon sx={{ mr: 0.5 }} />
                Select Type
              </Link>
              <Typography color="text.primary">
                {childDetails?.full_name}
              </Typography>
              <Typography color="text.primary">
                {therapyDetails?.name} Analysis
              </Typography>
            </Breadcrumbs>
            <Typography variant="h4" sx={{ fontWeight: "bold", mt: 1 }}>
              {therapyDetails?.name} Analysis for {childDetails?.full_name}
            </Typography>
          </Box>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {/* <Grid item xs={12} sm={4}>
              <StatCard
                title="Overall Progress"
                value={stats.overallProgress}
                icon={<BarChartIcon />}
                color="#29b6f6"
              />
            </Grid> */}
            <Grid item xs={12} sm={6}>
              <StatCard
                title="Total Sessions"
                value={stats.totalSessions}
                icon={<CalendarTodayIcon />}
                color="#66bb6a"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <StatCard
                title="Last Assessment"
                value={stats.lastAssessment}
                icon={<CheckCircleOutlineIcon />}
                color="#ffa726"
              />
            </Grid>
          </Grid>
          <Grid container spacing={4}>
            <Grid item xs={12} lg={8}>
              <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, borderRadius: "12px" }}>
                <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>
                  Activities
                </Typography>
                <Grid container spacing={3}>
                  {activeActivities.map((activity) => {
                    const todayIso = new Date().toISOString().slice(0, 10);
                    const todayEntry = activity.history.find(h => h.session_date === todayIso && h.scorer_type === "doctor");
                    return (
                      <Grid item xs={12} sm={6} md={4} key={activity.id}>
                        <ActivityCard
                          activity={activity}
                          hasScoredToday={!!todayEntry}
                          todayScore={todayEntry ? todayEntry.score : null}
                          onAddScore={handleAddScoreClick}
                          onViewDetails={handleViewDetailsClick}
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              </Paper>
              <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, borderRadius: "12px", mt: { xs: 2, sm: 3 }, mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>
                  Doctor Therapy Progress
                </Typography>
                <Box sx={{ height: isMobile ? 300 : 400, width: "100%" }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ top: 10, right: isMobile ? 20 : 30, left: isMobile ? 0 : 0, bottom: isMobile ? 20 : 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fill: '#666', fontSize: isMobile ? 10 : 12 }} 
                        axisLine={false} 
                        tickLine={false}
                        angle={isMobile ? -45 : 0}
                        textAnchor={isMobile ? "end" : "middle"}
                        interval={isMobile ? "preserveStartEnd" : 0}
                      />
                      <YAxis tick={{ fill: '#666', fontSize: isMobile ? 10 : 12 }} axisLine={false} tickLine={false} />
                      <Tooltip 
                        isAnimationActive={false}
                        contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }} 
                      />
                      <Legend 
                        iconType="circle" 
                        wrapperStyle={{ 
                          paddingTop: isMobile ? '30px' : '20px',
                          fontSize: isMobile ? '10px' : '12px'
                        }} 
                        layout={isMobile ? "horizontal" : "horizontal"}
                        verticalAlign="bottom"
                        align="center"
                      />
                      {activeActivities.slice(0, 5).map((act, index) => (
                        <Line
                          key={act.id}
                          type="monotone"
                          dataKey={act.name}
                          stroke={["#8884d8", "#82ca9d", "#ffc658", "#ff7300", "#413ea0"][index % 5]}
                          strokeWidth={3}
                          dot={{ r: 4, strokeWidth: 2 }}
                          activeDot={{ r: isMobile ? 8 : 6 }}
                          connectNulls={true}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
              <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, borderRadius: "12px", mt: { xs: 2, sm: 3 } }}>
                <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>
                  Parent vs Doctor Comparison
                </Typography>
                <Box sx={{ height: isMobile ? 350 : 400, width: "100%" }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={comparisonChartData} margin={{ top: 10, right: isMobile ? 20 : 30, left: isMobile ? 0 : 0, bottom: isMobile ? 30 : 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fill: '#666', fontSize: isMobile ? 10 : 12 }} 
                        axisLine={false} 
                        tickLine={false}
                        angle={isMobile ? -45 : 0}
                        textAnchor={isMobile ? "end" : "middle"}
                        interval={isMobile ? "preserveStartEnd" : 0}
                      />
                      <YAxis tick={{ fill: '#666', fontSize: isMobile ? 10 : 12 }} axisLine={false} tickLine={false} />
                      <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }} />
                      <Legend 
                        iconType="circle" 
                        wrapperStyle={{ 
                          paddingTop: isMobile ? '40px' : '20px',
                          fontSize: isMobile ? '9px' : '11px'
                        }} 
                        verticalAlign="bottom"
                        align="center"
                      />
                      {activeActivities.slice(0, 5).map((act, index) => {
                        const color = ["#8884d8", "#82ca9d", "#ffc658", "#ff7300", "#413ea0"][index % 5];
                        return (
                          <React.Fragment key={act.id}>
                            <Line type="monotone" dataKey={`${act.name} (Doc)`} stroke={color} strokeWidth={3} dot={{ r: 4, strokeWidth: 2 }} activeDot={{ r: isMobile ? 8 : 6 }} connectNulls={true} />
                            <Line type="monotone" dataKey={`${act.name} (Parent)`} stroke={color} strokeWidth={3} strokeDasharray="5 5" dot={{ r: 4, strokeWidth: 2 }} activeDot={{ r: isMobile ? 8 : 6 }} connectNulls={true} />
                          </React.Fragment>
                        );
                      })}
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} lg={4}>
              <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, borderRadius: "12px", mb: 3 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    display: "flex",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <InfoOutlinedIcon sx={{ mr: 1 }} />
                  How This Analysis Works
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Only activities scored by a doctor appear here. Add new
                  activities via 'Change Activities' and submit a score to make
                  them permanent for parents to see.
                </Typography>
              </Paper>
              <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, borderRadius: "12px" }}>
                <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>
                  Current Performance
                </Typography>
                <Box sx={{ height: isMobile ? 300 : 400 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      layout="vertical"
                      data={activeActivities}
                      margin={{ top: 5, right: 20, bottom: 5, left: isMobile ? -10 : 20 }}
                    >
                      <CartesianGrid stroke="#f5f5f5" />
                      <XAxis type="number" domain={[0, 10]} tick={{ fontSize: isMobile ? 10 : 12 }} />
                      <YAxis
                        dataKey="name"
                        type="category"
                        scale="band"
                        width={isMobile ? 80 : 100}
                        tick={{ fontSize: isMobile ? 9 : 12 }}
                      />
                      <Tooltip />
                      <Bar dataKey="currentScore" barSize={20} fill="#413ea0" />
                    </ComposedChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
        <Box
          className="no-print"
          sx={{ mt: 4, display: "flex", flexDirection: { xs: 'column-reverse', sm: 'row' }, justifyContent: "flex-end", gap: 2 }}
        >
          <Button variant="outlined" component={Link} to="/doctor-dashboard">
            Back to Dashboard
          </Button>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => setChangeModalOpen(true)}
          >
            Change Activities
          </Button>
          <Button
            variant="contained"
            startIcon={<FileDownloadIcon />}
            onClick={handleDownloadPdf}
          >
            Download PDF
          </Button>
        </Box>
      </Container>
      <Dialog open={scoreModalOpen} onClose={handleModalClose}>
        <DialogTitle>{isEditingScore ? "Edit Score for" : "Add Score for"} {selectedActivity?.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, px: 2, minWidth: 300, textAlign: 'center' }}>
            <Typography variant="h3" color="primary" sx={{ fontWeight: 'bold', mb: 2 }}>
              {newScore || 0}
            </Typography>
            <TextField
              type="number"
              inputProps={{ min: 0, max: 10, step: "0.1" }}
              value={newScore}
              onChange={(e) => {
                const val = e.target.value;
                if (val === '' || (Number(val) >= 0 && Number(val) <= 10)) {
                  setNewScore(val);
                }
              }}
              fullWidth
              variant="outlined"
              placeholder="Enter score (0-10, decimals allowed)"
              autoFocus
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
              Enter a score from 0 to 10. Decimals are allowed.
            </Typography>
            <TextField
              type="date"
              label="Session Date"
              value={sessionDate}
              onChange={(e) => setSessionDate(e.target.value)}
              fullWidth
              variant="outlined"
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleModalClose}>Cancel</Button>
          <Button onClick={handleScoreSubmit} variant="contained">
            Submit
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={detailsModalOpen}
        onClose={handleModalClose}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Score History for {selectedActivity?.name}</DialogTitle>
        <DialogContent>
          <List>
            {selectedActivity?.history
              .map((item, index) => (
                <React.Fragment key={index}>
                  <ListItem>
                    <ListItemText
                      primary={
                        <>
                          {`Score: ${item.score}`}{" "}
                          <Chip
                            label={item.scorer_type}
                            size="small"
                            color={
                              item.scorer_type === "doctor"
                                ? "primary"
                                : "success"
                            }
                            sx={{ ml: 1 }}
                          />{" "}
                        </>
                      }
                      secondary={new Date(
                        item.session_date
                      ).toLocaleDateString()}
                    />
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))
              .reverse()}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleModalClose}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={changeModalOpen}
        onClose={handleModalClose}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Change Activities</DialogTitle>
        <DialogContent>
          <FormGroup>
            {allAvailableActivities.map((act) => (
              <FormControlLabel
                key={act.id}
                control={
                  <Checkbox
                    checked={activitiesToChange.has(act.id)}
                    onChange={handleActivitySelectionChange}
                    name={String(act.id)}
                  />
                }
                label={act.name}
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleModalClose}>Cancel</Button>
          <Button onClick={handleSaveChanges} variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
const RouterLink = Link;
export default Therapy;
